import { useReduxState } from 'hooks';
import { getMandatoryReasonTexts } from 'lib/courses';
import { MandatoryReasonsTooltip } from 'components/common';
import { Icon } from 'components/common/icons';

import type { MandatoryReasons } from 'shared-types';

interface Props {
  mandatoryReasons?: MandatoryReasons;
  showCourseReason?: boolean;
}

// "Mandatory", or nothing when the course is not mandatory. When a group or
// the user's own setting makes the course mandatory, an icon next to the label
// lists those reasons in a tooltip. The course setting is left out unless
// showCourseReason is set, since in a list of users of one course it would be
// the same for everyone.
const CourseUserMandatory = ({
  mandatoryReasons,
  showCourseReason = false
}: Props) => {
  const { messages, theme } = useReduxState();

  if (!mandatoryReasons?.isMandatory) return null;

  const reasonTexts = getMandatoryReasonTexts({
    mandatoryReasons: {
      ...mandatoryReasons,
      isCourseMandatory: showCourseReason && mandatoryReasons.isCourseMandatory
    },
    messages
  });

  return (
    <span className="mandatory">
      {messages.commonMandatory}

      {reasonTexts.length ? (
        <MandatoryReasonsTooltip reasonTexts={reasonTexts}>
          {/* focusable so keyboard users can open the reasons */}
          <span
            aria-label={reasonTexts.join('. ')}
            className="edited"
            tabIndex={0}
          >
            <Icon icon="pen" />
          </span>
        </MandatoryReasonsTooltip>
      ) : null}

      <style jsx>{`
        .mandatory {
          align-items: center;
          display: inline-flex;
        }
        .edited {
          background-color: ${theme.warningLightColor};
          border-radius: 100px;
          color: ${theme.warningLightTextColor};
          display: inline-block;
          font-size: 0.8rem;
          height: 16px;
          margin: 3px 0 3px 5px;
          padding: 3px 0;
          text-align: center;
          width: 16px;
        }
      `}</style>
    </span>
  );
};

export default CourseUserMandatory;
